import _ from "lodash";
import momentTZ from "moment-timezone";

/**
 * Hook personalizado para convertir una fecha a múltiples zonas horarias y obtener las horas y nombres de las zonas horarias.
 *
 * Este hook realiza las siguientes acciones:
 * - Define una función `convertToTimeZone` que convierte una fecha de una zona horaria de origen a una zona horaria objetivo y la formatea.
 * - Define una función `getHour` que obtiene la hora formateada de una fecha.
 * - Define una función `getTimeZoneName` que obtiene el nombre de la zona horaria de una fecha.
 * - Define un objeto `timezones` que contiene las zonas horarias de interés.
 * - Define un objeto `dates` que contiene las fechas convertidas a las zonas horarias definidas en `timezones`.
 * - Crea un clon profundo del objeto `dates` llamado `hours` y lo actualiza con las horas formateadas correspondientes a cada zona horaria.
 * - Define un objeto `timezonesInfo` que contiene los nombres de las zonas horarias correspondientes a cada fecha convertida.
 *
 * @param {Object} params - Parámetros para la función.
 * @param {string} params.fullDate - La fecha completa que se convertirá.
 * @param {string} params.timeZoneSource - La zona horaria de origen de la fecha proporcionada.
 *
 * @returns {Object} - Un objeto que contiene:
 *    - `dates`: Un objeto con las fechas convertidas a cada zona horaria.
 *    - `hours`: Un objeto con las horas formateadas correspondientes a cada zona horaria.
 *    - `timezonesInfo`: Un objeto con los nombres de las zonas horarias correspondientes a cada fecha convertida.
 */

export const useTimeZonesDates = ({ fullDate, timeZoneSource }) => {
	
    const convertToTimeZone = (date, sourceTimeZone, targetTimeZone) => {
        return momentTZ.tz(date, sourceTimeZone).tz(targetTimeZone).format("YYYY-MM-DD hh:mm A");
    };

    const getHour = ({ date }) => {
        return momentTZ(date).format("h:mm A");
    };

    const getTimeZoneName = (date, timeZone) => {
        return momentTZ.tz(date, timeZone).format('z');
    };

    const timezones = {
        colombia: "America/Bogota",
        mexico: "America/Mexico_City",
        argentina: "America/Argentina/Buenos_Aires",
        chile: "America/Santiago",
        usa: "America/Chicago",
    };

    const dates = {
        colombia: convertToTimeZone(fullDate, timeZoneSource, timezones.colombia),
        mexico: convertToTimeZone(fullDate, timeZoneSource, timezones.mexico),
        argentina: convertToTimeZone(fullDate, timeZoneSource, timezones.argentina),
        chile: convertToTimeZone(fullDate, timeZoneSource, timezones.chile),
        usa: convertToTimeZone(fullDate, timeZoneSource, timezones.usa),
    };

    const hours = _.cloneDeep(dates);
    hours.colombia = getHour({ date: dates.colombia });
    hours.mexico = getHour({ date: dates.mexico });
    hours.argentina = getHour({ date: dates.argentina });
    hours.chile = getHour({ date: dates.chile });
    hours.usa = getHour({ date: dates.usa });

    const timezonesInfo = {
        colombia: getTimeZoneName(dates.colombia, timezones.colombia),
        mexico: getTimeZoneName(dates.mexico, timezones.mexico),
        argentina: getTimeZoneName(dates.argentina, timezones.argentina),
        chile: getTimeZoneName(dates.chile, timezones.chile),
        usa: getTimeZoneName(dates.usa, timezones.usa),
    };

    return {
        dates,
        hours,
        timezonesInfo,
    };
};
